import { WSRoute } from './types';

export const STORAGE_EVENT = 'on_storage_update';
export const ACTIVE_CONNECTIONS_KEY = 'ws_active_connections';

export interface ActiveConnectionEntry {
  route: WSRoute;
  id?: number;
  storageKey: string;
  status: 'CONNECTING' | 'OPEN' | 'CLOSED' | 'RECONNECTING';
  registeredAt: number;
}

function dispatch(key: string): void {
  window.dispatchEvent(new CustomEvent(STORAGE_EVENT, { detail: { key } }));
}

export const storage = {
  get<T>(key: string): T | null {
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : null;
    } catch {
      return null;
    }
  },

  set<T>(key: string, value: T): void {
    localStorage.setItem(key, JSON.stringify(value));
    dispatch(key);
  },

  remove(key: string): void {
    localStorage.removeItem(key);
    dispatch(key);
  },

  subscribe(listener: (key: string | null) => void): () => void {
    const handler = (e: Event) => {
      listener((e as CustomEvent<{ key: string }>).detail?.key ?? null);
    };
    const nativeHandler = (e: StorageEvent) => {
      listener(e.key);
    };
    window.addEventListener(STORAGE_EVENT, handler);
    window.addEventListener('storage', nativeHandler);
    return () => {
      window.removeEventListener(STORAGE_EVENT, handler);
      window.removeEventListener('storage', nativeHandler);
    };
  },
};

export const activeConnections = {
  list(): ActiveConnectionEntry[] {
    return storage.get<ActiveConnectionEntry[]>(ACTIVE_CONNECTIONS_KEY) ?? [];
  },

  upsert(entry: ActiveConnectionEntry): void {
    const current = this.list();
    const idx = current.findIndex((e) => e.storageKey === entry.storageKey);
    if (idx >= 0) {
      current[idx] = entry;
    } else {
      current.push(entry);
    }
    storage.set(ACTIVE_CONNECTIONS_KEY, current);
  },

  remove(storageKey: string): void {
    const filtered = this.list().filter((e) => e.storageKey !== storageKey);
    storage.set(ACTIVE_CONNECTIONS_KEY, filtered);
  },

  clear(): void {
    storage.set(ACTIVE_CONNECTIONS_KEY, []);
  },
};
